'use client'

import { Dialog } from '@acko/dialog'
import { Drawer } from '@acko/drawer'
import { Typography } from '@acko/typography'
import { useEffect, useState } from 'react'
import { BikeRegistrationForm, type BikeRegistrationFormProps } from './BikeRegistrationForm'
import styles from './page.module.css'

export type BikeRegistrationInputModalPresentation = 'modal' | 'bottom-sheet'

const imgCircleArrowLine = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Circle_arrow_line_882d3f586d.svg'
const imgCircleArrowFilled = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Circle_arrow_filled_9d8fd2ddfd.svg'

interface BikeRegistrationInputModalProps {
  onClose: () => void
  onNewBikeClick: () => Promise<void> | void
  presentation?: BikeRegistrationInputModalPresentation
  registrationFormProps: BikeRegistrationFormProps
  show: boolean
  viewModel: any
}

export const BikeRegistrationInputModal = ({
  onClose,
  onNewBikeClick,
  registrationFormProps,
  show,
  viewModel,
}: BikeRegistrationInputModalProps) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const handleNewBikeKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      void onNewBikeClick()
    }
  }

  const widgetCards = (
    <>
      <BikeRegistrationForm {...registrationFormProps} heading={undefined} />
      <div
        className={`${styles.newBikeCard} group cursor-pointer transition-all duration-200 hover:shadow-lg`}
        role="button"
        tabIndex={0}
        onClick={() => {
          void onNewBikeClick()
        }}
        onKeyDown={handleNewBikeKeyDown}>
        <div className="flex flex-col gap-8">
          <Typography variant="heading-md" as="h3" color="primary">
            {viewModel.newFlowBanner.heading}
          </Typography>
          <Typography variant="body-md" color="secondary">
            {viewModel.newFlowBanner.subHeading}
          </Typography>
        </div>
        <div className="flex items-center justify-between gap-12 w-full">
          <div className={`${styles.chipGradient} flex gap-8 items-center pl-8 pr-12 py-8 rounded-full`}>
            <img
              alt={viewModel.newFlowBanner.iconAlt}
              className="w-24 h-24 object-contain shrink-0"
              src={viewModel.newFlowBanner.iconSrc}
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <Typography variant="body-md" as="span" color="primary" className={styles.planStartingPrice}>
              {viewModel.newFlowBanner.ctaText}
            </Typography>
          </div>
          <div className="relative w-24 h-24">
            <img
              alt=""
              className="w-24 h-24 object-contain transition-opacity duration-200 group-hover:opacity-0"
              src={imgCircleArrowLine}
            />
            <img
              alt=""
              className="absolute inset-0 w-24 h-24 object-contain transition-opacity duration-200 opacity-0 group-hover:opacity-100"
              src={imgCircleArrowFilled}
            />
          </div>
        </div>
      </div>
    </>
  )

  const title = registrationFormProps.heading ?? "Let's insure your bike"

  if (isMobile) {
    return (
      <Drawer
        open={show}
        onClose={onClose}
        side="bottom"
        title={title}
        className="!max-h-none [&_.acko-drawer-title]:text-[var(--font-heading-lg-size)] [&_.acko-drawer-title]:leading-[var(--font-heading-lg-line)] [&_.acko-drawer-title]:tracking-[var(--font-heading-lg-spacing)] [&_.acko-drawer-title]:[font-weight:var(--font-heading-lg-weight)]">
        <div className="flex flex-col gap-20">{widgetCards}</div>
      </Drawer>
    )
  }

  return (
    <Dialog
      open={show}
      onClose={onClose}
      title={title}
      className="!max-w-[520px] [&_.acko-dialog-title]:text-[var(--font-heading-lg-size)] [&_.acko-dialog-title]:leading-[var(--font-heading-lg-line)] [&_.acko-dialog-title]:tracking-[var(--font-heading-lg-spacing)] [&_.acko-dialog-title]:[font-weight:var(--font-heading-lg-weight)]">
      <div className="flex flex-col gap-20">{widgetCards}</div>
    </Dialog>
  )
}
